import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar, { isAsideKey } from './components/Sidebar';
import AppShell from './components/AppShell';
import { ReportingContext } from './components/ReportingControls';
import { pageLabel } from './nav/pageLabel';
import ComingSoon from './components/ComingSoon/ComingSoon';
import Toast from './components/Toast';
import SettingsHubPage from './pages/SettingsHubPage';
import AdminHubPage from './pages/admin/AdminHubPage';
import PeopleSynthesisPage from './pages/hr/PeopleSynthesisPage';
import PeopleStoryPage from './pages/hr/PeopleStoryPage';
import HrSocialHub from './pages/hr/HrSocialHub';
import HrCostSimulationPage from './pages/hr/HrCostSimulationPage';
import HrEvolutionPage from './pages/hr/HrEvolutionPage';
import HrYearEndSimulationPage from './pages/hr/HrYearEndSimulationPage';
import HrPlanningHub from './pages/hr/HrPlanningHub';
import PersonnelHub from './pages/hr/PersonnelHub';
import LabourRateHub from './pages/hr/LabourRateHub';
import TimeHub from './pages/hr/TimeHub';
import MappingPage from './pages/MappingPage';
import FinancialsInputHub from './pages/FinancialsInputHub';
import FinancialsMappingHub from './pages/FinancialsMappingHub';
import FinancialsSettingsHub from './pages/FinancialsSettingsHub';
import FinancialsReportsHub, { REPORT_STATES } from './pages/FinancialsReportsHub';
import BfreCommandPage from './pages/BfreCommandPage';
import FinancialsDataHub from './pages/FinancialsDataHub';
import TresorerieHub from './pages/TresorerieHub';
import { bandForPage } from './nav/activeBand';
import { planningLevel } from './nav/planningLevel';
import CapexPlanPage from './pages/financials/CapexPlanPage';
import WorkingCapitalPage from './pages/financials/WorkingCapitalPage';
import BudgetChargesPage from './pages/financials/BudgetChargesPage';
import SpecialOperationsPage from './pages/financials/SpecialOperationsPage';
import PayrollPage from './pages/financials/PayrollPage';
import BusinessPlanPage from './pages/financials/BusinessPlanPage';
import FinancingSection from './pages/financials/FinancingSection';
import ForecastStepPage, { isForecastStep } from './pages/ForecastStepPage';
import BalanceSheetCommandPage from './pages/BalanceSheetCommandPage';
import CashFlowCommandPage from './pages/CashFlowCommandPage';
import CustomReportsPage from './pages/CustomReportsPage';
import CfoHub from './pages/CfoHub';
import JournalPage from './pages/cockpit-cfo/JournalPage';
import PrevisionnelHub from './pages/PrevisionnelHub';
import AdjustmentsPage from './pages/AdjustmentsPage';
import BsMappingPage from './pages/BsMappingPage';
import AnalyticsHubPage from './pages/hr/AnalyticsHubPage';
import CostAssignmentPage from './pages/tdabc/CostAssignmentPage';
import RatesHub from './pages/RatesHub';
import IndirectCostsPage from './pages/tdabc/IndirectCostsPage';
import RentabiliteClientPage from './pages/tdabc/RentabiliteClientPage';
import CoutHeureTFHub from './pages/CoutHeureTFHub';
import TdabcPrintDocument from './pages/tdabc/TdabcPrintDocument';
import PeoplePrintDocument from './pages/hr/PeoplePrintDocument';
import PlanningPrintDocument from './pages/hr/PlanningPrintDocument';
import CostingPrintDocument from './pages/costing/CostingPrintDocument';
import PrintReportDocument from './pages/PrintReportDocument';
import TreasuryPage from './pages/treasury/TreasuryPage';
import SalesForecastPage from './pages/sales/SalesForecastPage';
import AtterrissagePlanPage from './pages/sales/AtterrissagePlanPage';
import SalesPlanPage from './pages/sales/SalesPlanPage';
import OfferMonthPage from './pages/sales/OfferMonthPage';
import PrevisionsPage from './pages/sales/previsions/PrevisionsPage';
import OfferPlanPage from './pages/sales/OfferPlanPage';
import ServicePlanPage from './pages/sales/ServicePlanPage';
import StockPurchasesSectionPage from './pages/StockPurchasesSectionPage';
import DirectCostsPage from './pages/financials/DirectCostsPage';
import SourceMapPage from './pages/sales/SourceMapPage';
import SalesBillingAnalysisPage from './pages/sales/SalesBillingAnalysisPage';
import WipHomePage from './pages/WipHomePage';
import TimePilotHub from './pages/hr/TimePilotHub';
import PayrollDetailHub from './pages/hr/PayrollDetailHub';
import HeadcountHub from './pages/hr/HeadcountHub';
import PeopleHomeHub from './pages/hr/PeopleHomeHub';
import WelcomePage from './pages/WelcomePage';
import WelcomeNewsPage from './pages/WelcomeNewsPage';
import WelcomeGuidesPage from './pages/WelcomeGuidesPage';
import WelcomeFichesPage from './pages/WelcomeFichesPage';
import KnowledgeDrawer from './components/knowledge/KnowledgeDrawer';
import FicheWindow from './components/knowledge/FicheWindow';
import GuideOverlay from './components/guide/GuideOverlay';
import { guide } from './components/guide/guideStore';
import { pageHasHelp } from './data/pageHelp';
import AuditLogPage from './pages/AuditLogPage';
import NewsAdminPage from './pages/NewsAdminPage';
import ErrorBoundary from './components/ErrorBoundary';
import NoModulesPage from './components/NoModulesPage';
import LoginPage from './pages/LoginPage';
import './pages/LoginPage.css';
import AssistantPanel from './components/AssistantPanel';
import Logo from './components/Logo';
import ForbiddenBar from './components/ForbiddenBar';
import { ReportNavContext } from './components/reportNav';
import { listTenants, deleteTenant, updateTenant } from './api/tenants';
import { login as apiLogin, logout as apiLogout, fetchMe } from './api/auth';
import { getAccessToken, getRefreshToken, clearTokens, setPlanVersion, planVersionStorageKey } from './api/client';
import { listPlanVersions } from './api/planVersions';
import { setOpsViaSop } from './api/opsRoute';
import {
  computeVisibleModules,
  computeLockedModules,
  canExportData,
  canManageSettings,
  filterNav,
  isPageAllowed,
  moduleForPage,
  ROLE_LABEL_KEYS,
} from './nav/entitlements';
import './App.css';

/* Écran de démarrage — la marque une seconde au chargement de l'app
   (symbole + « Palestre »), puis fondu (décision 2026-08-16 : le nom se voit
   aux moments qui ne coûtent rien au design). */
function BrandSplash() {
  const [phase, setPhase] = useState('on');
  useEffect(() => {
    const t1 = setTimeout(() => setPhase('fade'), 1100);
    const t2 = setTimeout(() => setPhase('off'), 1650);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);
  if (phase === 'off') return null;
  return (
    <div className={`brand-splash${phase === 'fade' ? ' is-fading' : ''}`} aria-hidden>
      <div className="brand-splash__lockup">
        <Logo size={64} />
        <span>Palestre</span>
      </div>
    </div>
  );
}

/* « Saisie des temps » sert CINQ onglets du hub Temps : la vue d'ensemble de la
   capacité, la cascade standard, les heures TF de l'année, leur ventilation par
   activité, puis le détail mensuel du facturable. Constante hissée hors du composant — un tableau
   recréé à chaque rendu relancerait l'effet qui recale l'onglet actif. */
const TIME_ENTRY_TABS = ['overview', 'standard', 'production', 'allocation', 'production-months'];

/* Les deux sections du Cockpit qui montent la page des NOTES (2026-10-01). */
const JOURNAL_TABS = new Set(['journal', 'decisions']);

/* People : ce qui se VERSE (salaires, compléments, réglages du contrat) et ce
   qui se REMBOURSE (frais, indemnités reçues) tiennent deux sections, un seul
   hub (2026-09-01). */
const SALARY_TABS = ['people', 'contracts', 'extras', 'general'];
const EXPENSE_TABS = ['expenses', 'received'];

/* People : « Prévisionnel » chiffre l'exercice, « Trajectoire » regarde
   au-delà. Un seul hub, deux listes d'onglets, hissées hors du composant. */
/* « Charges » (ex-Prévisionnel) porte aussi le DÉTAIL PAR PERSONNE depuis le
   2026-09-18 : il zoomait sur la Vue d'ensemble, qui regarde l'année entière ;
   il appartient à l'écran où l'on chiffre, et il y vient en dernier — du global
   au nominatif. */
const FORECAST_TABS = ['payroll', 'social', 'detail'];
const TRAJECTORY_TABS = ['trajectory', 'multiyear'];

/* Finance : LES ÉTATS EN ACCÈS DIRECT (2026-09-17) — une clé de page par
   section du menu, un même composant derrière. « Indicateurs financiers » en
   porte deux (BFRE et ratio de liquidité), sous un sélecteur. */
const FIN_STATE_PAGES = {
  'financials-synthesis': REPORT_STATES.synthesis,
  'financials-pl': REPORT_STATES.pl,
  'financials-bs': REPORT_STATES.bs,
  'financials-cf': REPORT_STATES.cf,
  'financials-cash-plan': REPORT_STATES.cashPlan,
  'financials-indicators': REPORT_STATES.indicators,
};


export default function App() {
  const { t } = useTranslation();
  const [authReady, setAuthReady] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [authUser, setAuthUser] = useState(null);
  const [authScopes, setAuthScopes] = useState({});
  /* LE RÔLE OPÉRATIONS (2026-09-29) : sans la finance, ses écrans d'Opérations
     passent par `/api/sop`. Posé à chaque rendu, avant que les pages chargent. */
  setOpsViaSop(!['r', 'rw'].includes(authScopes?.financials) && ['r', 'rw'].includes(authScopes?.sales));
  const [authMemberships, setAuthMemberships] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [activeTenantId, setActiveTenantId] = useState(null);
  /* VERSION DE PLAN (2026-09-27) : celle que le client actif lit et modifie.
     Null = la version courante du client (le serveur la choisit). Retenue par
     client dans le navigateur, posée sur le client d'API AVANT que le client
     change, pour que la première requête des écrans la porte déjà. */
  const [planVersionId, setPlanVersionIdState] = useState(null);
  const [planVersions, setPlanVersions] = useState([]);
  function applyPlanVersion(tid, vid) {
    const stored = tid ? (vid === undefined ? localStorage.getItem(planVersionStorageKey(tid)) : vid) : null;
    setPlanVersion(tid, stored);
    setPlanVersionIdState(stored || null);
    if (!tid) return;
    if (stored) localStorage.setItem(planVersionStorageKey(tid), stored);
    else localStorage.removeItem(planVersionStorageKey(tid));
  }
  const reloadPlanVersions = useCallback(async (tid) => {
    const id = tid || activeTenantId;
    if (!id) { setPlanVersions([]); return; }
    try {
      const res = await listPlanVersions(id);
      const list = res.data || [];
      setPlanVersions(list);
      const kept = localStorage.getItem(planVersionStorageKey(id));
      // Une version retenue puis supprimée ailleurs : retour à la courante.
      if (kept && !list.some((v) => v.id === kept)) applyPlanVersion(id, null);
    } catch (_e) {
      setPlanVersions([]);
    }
  }, [activeTenantId]);
  useEffect(() => { reloadPlanVersions(activeTenantId); }, [activeTenantId, reloadPlanVersions]);
  // La version retenue a été supprimée ailleurs (404 du serveur) : retour à la courante.
  useEffect(() => {
    const onGone = () => {
      if (!activeTenantId) return;
      applyPlanVersion(activeTenantId, null);
      reloadPlanVersions(activeTenantId);
    };
    window.addEventListener('palestre:plan-version-gone', onGone);
    return () => window.removeEventListener('palestre:plan-version-gone', onGone);
  }, [activeTenantId, reloadPlanVersions]);
  /* Dernière page ouverte, restaurée au rafraîchissement (demande Olivier
     2026-08-17) : recharger ramenait toujours au Cockpit. Si la page n'est plus
     autorisée (bascule de client, droits), la garde ci-dessous la ramène au
     Cockpit — donc aucun risque d'écran interdit. */
  const [page, setPageState] = useState(() => {
    /* Sans page mémorisée (première venue, navigateur neuf), on ouvre
       l'ACCUEIL : la page que tout le monde a (2026-09-16). */
    try { return window.localStorage.getItem('palestre_last_page') || 'welcome'; } catch (e) { return 'welcome'; }
  });
  /* Onglet demandé par un lien INTER-SECTIONS (2026-08-23) : `setPage(clé,
     onglet)` dépose l'intention, la section cible la consomme au montage. Même
     idée que `peopleIntent` plus bas, en générique. Remise à null à CHAQUE
     navigation : sinon revenir par le menu rouvrirait le dernier onglet visé. */
  const [navTab, setNavTab] = useState(null);
  const setPage = React.useCallback((next, tab = null) => {
    setNavTab(tab);
    setPageState(next);
    try { if (typeof next === 'string') window.localStorage.setItem('palestre_last_page', next); } catch (e) { /* quota */ }
  }, []);
  // Statut de complétude des 3 hubs d'Input (pastilles sidebar), par tenant×année.
  // Sidebar épinglée (ouverte bloquée) vs repli auto au survol.
  const [sbPinned, setSbPinned] = useState(() => localStorage.getItem('palestre_sb_pinned') === '1');
  /* Intention de navigation People (2026-08-21) : une section peut demander un
     ONGLET et parfois une MODALE dans la section Contrats. On la dépose ici, la
     section la consomme puis l'efface — sinon y revenir rouvrirait la modale à
     chaque fois. L'émetteur est la fenêtre « Données People » ouverte depuis
     l'accueil du module : « Ajouter un collaborateur » n'importe rien, il faut
     donc l'envoyer au formulaire de saisie, onglet et modale ouverts. */
  const [peopleIntent, setPeopleIntent] = useState(null);
  const [headcountIntent, setHeadcountIntent] = useState(null);
  function goPeople(intent) { setPeopleIntent(intent); setPage('hr-personnel'); }
  // Même mécanique vers les Effectifs : l'Accueil pose un mouvement à distance.
  function goHeadcount(intent) { setHeadcountIntent(intent); setPage('hr-headcount'); }
  /* Vers une section des PARAMÈTRES du client (2026-09-27) : un écran qui lit
     un réglage (le taux de TVA) y renvoie, et le panneau des Paramètres porte
     « Fermer et revenir à {écran} » pour y retourner. */
  function goSettings(section) { setSettingsTab(section); setPage('settings-hub'); }

  function toggleSbPin() {
    setSbPinned(v => { const n = !v; localStorage.setItem('palestre_sb_pinned', n ? '1' : '0'); return n; });
  }
  // Onglet/sous-page actif de la Prévision trésorerie (Finance → TreasuryPage).
  // Piloté par TreasuryPage (onTabChange) ; onGoCashTab reste dispo pour les
  // accès directs (Cockpit). Plus de barre d'onglets interne au menu.
  const [cashTab, setCashTab] = useState('synthesis');
  // Vue active du Cockpit (ex-onglets) : pilotée par le panneau contextuel de la
  // Sidebar, comme les hubs Paramètres / Administration.
  const [cockpitTab, setCockpitTab] = useState('rent');
  // Section active des hubs Paramètres / Administration : pilotée par le panneau
  // contextuel de la Sidebar (comme cashTab pour la Trésorerie).
  const [settingsTab, setSettingsTab] = useState('general');
  const [adminTab, setAdminTab] = useState('tenants');
  const [toast, setToast] = useState(null);
  const [printingTdabc, setPrintingTdabc] = useState(false);
  const [printingPeople, setPrintingPeople] = useState(false);
  // Guide « Prise en main » : monté ICI (pas dans la page) pour survivre aux
  // navigations que ses étapes déclenchent.
  /* Panneau Savoirs, mode Guide et fenêtre de fiche (2026-09-16). Le panneau
     s'ouvre par l'ampoule du rail et reste ouvert pendant « Me montrer » : il
     pousse la page, il ne la couvre pas. Une fiche se lit dans sa fenêtre,
     depuis le panneau comme depuis son repère. */
  const [knowledgeOpen, setKnowledgeOpen] = useState(false);
  const [ficheId, setFicheId] = useState(null);
  useEffect(() => { guide.setPage(page); }, [page]);
  const [printingPlanning, setPrintingPlanning] = useState(false);
  // Pack Costing : null | 'pdf' | 'html'
  const [costingExport, setCostingExport] = useState(null);
  const [printReports, setPrintReports] = useState(null);  // array de clés | null
  // Année active partagée entre toutes les vues (People / Time / CHR / etc.)
  // Persistée localement pour traverser les changements de page et reloads.
  const [activeYear, setActiveYearState] = useState(() => {
    const saved = parseInt(localStorage.getItem('palestre_active_year'), 10);
    return Number.isFinite(saved) && saved >= 2000 && saved <= 2100
      ? saved
      : new Date().getFullYear();
  });
  function setActiveYear(y) {
    const n = Number(y);
    if (!Number.isFinite(n)) return;
    setActiveYearState(n);
    localStorage.setItem('palestre_active_year', String(n));
  }

  function handleGoCashTab(tabKey) {
    setCashTab(tabKey);
    setPage('treasury');
  }

  function handleGoCockpitTab(tabKey) {
    setCockpitTab(tabKey);
    setPage('home');
  }

  // Caches métier gardés dans le navigateur (prototypes et filets locaux). Ils
  // survivaient à la déconnexion : la personne suivante sur le même poste
  // retrouvait les chiffres du client précédent, hors de toute règle de droits.
  function purgeLocalBusinessCaches() {
    try {
      Object.keys(window.localStorage)
        .filter((k) => k.startsWith('atterrissage_') || k.startsWith('atl_bak_'))
        .forEach((k) => window.localStorage.removeItem(k));
    } catch (e) { /* mode privé : rien à faire */ }
  }

  function clearSession() {
    purgeLocalBusinessCaches();
    setAuthed(false);
    setAuthUser(null);
    setAuthScopes({});
    setAuthMemberships([]);
    setTenants([]);
    applyPlanVersion(null);
    setActiveTenantId(null);
  }

  function applyMePayload(meData, preferredTenantId) {
    setAuthUser(meData.user);
    setAuthMemberships(meData.memberships || []);
    const memberships = meData.memberships || [];
    const tid = preferredTenantId
      || meData.tenant?.id
      || meData.user?.tenant_id
      || memberships[0]?.tenant_id;
    const m = memberships.find((x) => x.tenant_id === tid);
    setAuthScopes(m?.scopes || meData.scopes || {});
  }

  async function loadTenantsForUser(meData) {
    const tRes = await listTenants();
    if (tRes.data?.length) {
      setTenants(tRes.data);
      const saved = localStorage.getItem('palestre_tenant_id');
      const valid = tRes.data.find(tenant => tenant.id === saved);
      const preferred = meData?.tenant?.id;
      const tid = valid ? saved : (tRes.data.find(tenant => tenant.id === preferred)?.id || tRes.data[0].id);
      applyPlanVersion(tid);
      setActiveTenantId(tid);
      localStorage.setItem('palestre_tenant_id', tid);
      const m = (meData.memberships || []).find((x) => x.tenant_id === tid);
      if (m?.scopes) setAuthScopes(m.scopes);
    } else if (meData?.tenant) {
      setTenants([meData.tenant]);
      applyPlanVersion(meData.tenant.id);
      setActiveTenantId(meData.tenant.id);
      localStorage.setItem('palestre_tenant_id', meData.tenant.id);
    }
  }

  async function bootstrapSession() {
    if (!getAccessToken() && !getRefreshToken()) {
      clearSession();
      setAuthReady(true);
      return;
    }
    /* ⚠️ PAS DE `setPage` ICI (correction 2026-09-25). Cette fonction restaure
       une session existante : elle tourne donc à CHAQUE démarrage, rechargement
       compris, et poser l'Accueil revenait à traiter un rafraîchissement comme
       une connexion. L'Accueil se pose là où le mot de passe est validé, dans
       `handleLogin` ; ici on laisse la page mémorisée revenir (décision
       2026-08-17, que le commentaire annonçait sans que le code la tienne). */
    const me = await fetchMe();
    if (me.data?.user) {
      applyMePayload(me.data);
      setAuthed(true);
      await loadTenantsForUser(me.data);
    } else {
      clearTokens();
      clearSession();
    }
    setAuthReady(true);
  }

  useEffect(() => {
    bootstrapSession();
  }, []); // eslint-disable-line

  useEffect(() => {
    const onExpired = () => { clearSession(); };
    window.addEventListener('palestre:auth-expired', onExpired);
    return () => window.removeEventListener('palestre:auth-expired', onExpired);
  }, []);

  // Erreurs API non-404 remontées par unwrap (api/client.js) → toast global.
  useEffect(() => {
    const onApiError = (e) => addToast(e.detail?.error || 'Erreur réseau', e.detail?.kind || 'error', e.detail?.source);
    window.addEventListener('palestre:api-error', onApiError);
    return () => window.removeEventListener('palestre:api-error', onApiError);
  }, []); // eslint-disable-line

  const activeTenant = tenants.find(tenant => tenant.id === activeTenantId) || null;
  /* Jeu de couleurs PAR CLIENT (2026-09-01) : l'attribut à la racine bascule
     tous les tokens ; l'olive est le défaut de :root (2026-10-20), donc pas
     d'attribut sans client (page de connexion) ni pour un client en olive. */
  useEffect(() => {
    const t = activeTenant?.ui_theme;
    if (t && t !== 'olive') document.documentElement.dataset.uiTheme = t;
    else delete document.documentElement.dataset.uiTheme;
  }, [activeTenant?.ui_theme]);
  const isPlatformAdmin = Boolean(authUser?.is_platform_admin);
  // Rôle affiché = celui de l'appartenance au client actif (il change avec le
  // sélecteur de client), sinon le rôle « home » du compte.
  // Cumul multi-rôles : une personne peut porter plusieurs rôles sur un client.
  const activeRoleSlugs = useMemo(() => {
    const fromMemberships = authMemberships
      .filter(m => m.tenant_id === activeTenantId)
      .map(m => m.role)
      .filter(Boolean);
    if (fromMemberships.length) return fromMemberships;
    return authUser?.role ? [authUser.role] : [];
  }, [authMemberships, activeTenantId, authUser?.role]);
  const activeRoleSlug = activeRoleSlugs[0] || null;
  const activeRoleLabel = activeRoleSlug
    ? (ROLE_LABEL_KEYS[activeRoleSlug] ? t(ROLE_LABEL_KEYS[activeRoleSlug]) : activeRoleSlug)
    : null;

  const visibleModules = useMemo(
    () => computeVisibleModules(activeTenant?.enabled_modules, authScopes),
    [activeTenant?.enabled_modules, authScopes],
  );
  const lockedModules = useMemo(
    () => computeLockedModules(activeTenant?.enabled_modules, authScopes),
    [activeTenant?.enabled_modules, authScopes],
  );
  const filteredNav = useMemo(
    () => filterNav(visibleModules, authScopes, isPlatformAdmin),
    [visibleModules, authScopes, isPlatformAdmin],
  );
  /* Page d'où l'on a ouvert un lieu « À CÔTÉ » des modules : les réglages et
     les trois adresses de l'exploitant. Rien dans le menu n'y ramène, puisqu'on
     n'y arrive pas par un module ; le panneau la nomme donc et y renvoie. */
  /* Le contexte d'un message d'erreur, relu par `addToast` : l'écouteur des
     erreurs d'API est posé une fois, il ne verrait sinon que le premier rendu. */
  const toastContext = useRef({});
  toastContext.current = {
    screen: page ? `${pageLabel(filteredNav, page, t) || page} (${page})` : null,
    tenant: activeTenant?.name || null,
    year: activeYear,
  };
  const hubReturn = React.useRef('home');
  useEffect(() => {
    if (!isAsideKey(filteredNav, page)) hubReturn.current = page;
  }, [page, filteredNav]);

  // Deep-link / bascule tenant : page hors modules visibles → Cockpit.
  useEffect(() => {
    if (!authed) return;
    /* ⚠️ TANT QU'AUCUN CLIENT N'EST CHARGÉ, ON NE JUGE RIEN (2026-09-19) : au
       rafraîchissement, `activeTenant` arrive APRÈS le premier rendu, donc
       `visibleModules` est vide et toute page se lisait comme interdite. La
       garde renvoyait à l'Accueil et ÉCRASAIT la page mémorisée : on perdait
       son écran à chaque F5. */
    if (!activeTenant) return;
    if (!isPageAllowed(page, visibleModules, {
      isPlatformAdmin, scopes: authScopes, roles: activeRoleSlugs,
    })) {
      setPage('home');
    }
  }, [authed, activeTenant, page, visibleModules, isPlatformAdmin, authScopes]);

  // Apparence standard pour tous les clients : accent ardoise unique de la charte
  // (--color-accent). Plus de panachage par tenant — le seul repère par client est
  // la pastille de couleur en tête du rail (Sidebar), purement identifiante.

  // Titre d'onglet = client · année · Palestre → distingue les onglets/clients.
  useEffect(() => {
    document.title = activeTenant ? `${activeTenant.name} · ${activeYear} · Palestre` : 'Palestre';
  }, [activeTenant, activeYear]);

  async function handleTenantChange(id) {
    applyPlanVersion(id);
    setActiveTenantId(id);
    localStorage.setItem('palestre_tenant_id', id);
    const local = authMemberships.find((m) => m.tenant_id === id);
    if (local?.scopes) {
      setAuthScopes(local.scopes);
    } else {
      const me = await fetchMe(id);
      if (me.data) applyMePayload(me.data, id);
    }
  }

  async function refreshTenantsList() {
    const tRes = await listTenants();
    if (tRes.data) setTenants(tRes.data);
  }

  // Cockpit Notes : remonte le tenant patché pour rafraîchir activeTenant.notes.
  function handleTenantPatched(updatedTenant) {
    if (!updatedTenant?.id) return;
    setTenants(prev => prev.map(tenant => tenant.id === updatedTenant.id ? { ...tenant, ...updatedTenant } : tenant));
  }

  // Le Cockpit d'accueil EST désormais le hub CFO. On redirige les anciennes
  // routes ('cockpit' ComingSoon · 'cfo-hub' section Finance) vers 'home'.
  useEffect(() => {
    if (page === 'cockpit' || page === 'cfo-hub') setPage('home');
  }, [page]);

  async function handleRenameTenant(id) {
    const tenant = tenants.find(tn => tn.id === id);
    if (!tenant) return;
    const name = window.prompt("Renommer l'entreprise :", tenant.name);
    if (name == null) return;
    const trimmed = name.trim();
    if (!trimmed || trimmed === tenant.name) return;
    try {
      const res = await updateTenant(id, { name: trimmed });
      if (res.data) {
        handleTenantPatched(res.data);
        addToast(`Renommé en « ${res.data.name} ».`);
      } else {
        addToast(res.error || 'Erreur lors du renommage.', 'error');
      }
    } catch (err) {
      addToast(`Erreur : ${err.message}`, 'error');
    }
  }

  async function handleLogin(email, password) {
    const res = await apiLogin(email, password);
    if (!res.data?.access_token) {
      return res.error || 'Email ou mot de passe incorrect.';
    }
    /* ON ARRIVE SUR L'ACCUEIL (2026-09-16) : une session qui S'OUVRE commence
       par les nouveautés et les guides. C'est ici, et nulle part ailleurs, que
       ce moment se reconnaît : le mot de passe vient d'être validé. */
    setPage('welcome');
    const me = await fetchMe();
    if (me.data?.user) {
      applyMePayload(me.data);
      setAuthed(true);
      await loadTenantsForUser(me.data);
    } else {
      setAuthUser(res.data.user);
      setAuthScopes({});
      setAuthMemberships([]);
      setAuthed(true);
      await loadTenantsForUser({ user: res.data.user });
    }
    return null;
  }

  async function handleLogout() {
    await apiLogout();
    clearSession();
    setPage('home');
  }

  async function handleDeleteTenant(id) {
    const tenant = tenants.find(tn => tn.id === id);
    if (!tenant) return false;
    // La confirmation (sauvegardes téléchargées + saisie « DELETE ») est portée par
    // DeleteTenantModal ; ici on exécute directement.
    try {
      const res = await deleteTenant(id);
      if (res.data || res.error === null) {
        addToast(`Entreprise « ${tenant.name} » supprimée.`);
        // Déconnexion UNIQUEMENT si on vient de supprimer le client sous ses
        // propres pieds. Depuis la liste d'Administration, on supprime un AUTRE
        // client : la session reste valide, on rafraîchit simplement la liste.
        if (id === activeTenantId) {
          await handleLogout();
        } else {
          await refreshTenantsList();
        }
        return true;
      }
      addToast(res.error || 'Erreur lors de la suppression.', 'error');
    } catch (err) {
      addToast(`Erreur : ${err.message}`, 'error');
    }
    return false;
  }

  /* `source` dit d'où vient une erreur d'API (requête, statut) ; l'écran, le
     client et l'exercice sont pris au moment du message, pour le bouton
     « Copier » du message d'erreur. */
  /* Le même message redit aussitôt par l'écran (qui relaie souvent l'erreur
     que le client API vient d'afficher) ne remplace pas le premier : celui-ci
     porte la SOURCE que « Copier » recopie, et sa nature (information ou
     erreur). */
  const lastToast = useRef(null);
  function addToast(msg, type = 'ok', source = null) {
    const at = Date.now();
    const prev = lastToast.current;
    if (prev && prev.msg === msg && at - prev.at < 1500 && type !== 'ok') return;
    lastToast.current = { msg, at };
    setToast({ msg, type, source, context: { ...toastContext.current }, at, id: at });
  }

  if (!authReady) {
    return <div className="login-page"><div className="login-card">Chargement…</div></div>;
  }

  if (!authed) {
    return <LoginPage onLogin={handleLogin} />;
  }

  return (
    <ReportingContext.Provider value={{
      year: activeYear,
      onYearChange: setActiveYear,
      closed: activeTenant?.last_closed_period || null,
      /* La version de plan se lit dans les deux modules qui planifient,
         Ventes & Ops et Finance, et sur l'Accueil. Ailleurs, le sélecteur
         n'a rien à dire. */
      versions: planVersions,
      versionId: planVersionId,
      onVersionChange: (vid) => applyPlanVersion(activeTenantId, vid),
      reloadVersions: () => reloadPlanVersions(activeTenantId),
      /* « Modifier / nouveau… », en pied du sélecteur de version (2026-10-03) :
         les versions se gèrent dans Paramètres › Planification. */
      onManageVersions: () => goSettings('planning'),
      /* L'Accueil aussi (2026-10-09) : ses cartes lisent le plan. Le choix
         est celui du navigateur, le même dans tous les modules. */
      showVersion: ['scenarii', 'financials'].includes(moduleForPage(page))
        || (page === 'welcome' && visibleModules.some((m) => m === 'scenarii' || m === 'financials')),
      /* MANUEL ⇄ PRO D'UN CLIC (demande d'Olivier, 2026-09-30), depuis la
         pastille de la barre supérieure ; le même réglage que Paramètres ›
         Planification. Pro demande Finance. */
      onPlanLevelToggle: async () => {
        const next = planningLevel(activeTenant) === 'pro' ? 'manual' : 'pro';
        const res = await updateTenant(activeTenantId, { planning_level: next });
        if (res && res.data) handleTenantPatched(res.data);
        else addToast((res && res.error) || t('common:errors.generic'), 'error');
      },
    }}>
      <div className="app-shell">
      <BrandSplash />
      {/* LE CADRE : bandeau des modules, menu, ligne de commandes. Il ne
          connaît aucune page — App ne lui passe que l'état de navigation et la
          configuration NAV, et lui confie le contenu en enfant. */}
      <AppShell
        backKey={isAsideKey(filteredNav, page) ? page : null}
        backLabel={pageLabel(filteredNav, hubReturn.current, t)}
        onBack={() => setPage(hubReturn.current || 'home')}
        page={page} setPage={setPage} nav={filteredNav} lockedModules={lockedModules} scopes={authScopes}
        isPlatformAdmin={isPlatformAdmin}
        canManageSettings={canManageSettings(activeRoleSlugs, isPlatformAdmin)}
        isTenantAdmin={isPlatformAdmin || activeRoleSlugs.includes('admin')}
        canExportData={canExportData(activeRoleSlugs, authScopes, isPlatformAdmin)}
        tenants={tenants} activeTenant={activeTenant} onTenantChange={handleTenantChange}
        onLogout={handleLogout}
        userEmail={authUser?.email || null} userName={authUser?.name || null}
        userRoleLabel={activeRoleLabel}
        pinned={sbPinned} onTogglePin={toggleSbPin}
        settingsTab={settingsTab} onGoSettingsTab={setSettingsTab}
        adminTab={adminTab} onGoAdminTab={setAdminTab}
        cockpitTab={cockpitTab} onGoCockpitTab={handleGoCockpitTab}
        cashTab={cashTab} onGoCashTab={handleGoCashTab}
        knowledgeOpen={knowledgeOpen}
        onToggleKnowledge={() => setKnowledgeOpen((v) => !v)}
        knowledgeDot={pageHasHelp(page)}
        banner={<ForbiddenBar />}
        rightPanel={(
          <KnowledgeDrawer open={knowledgeOpen} onClose={() => setKnowledgeOpen(false)}
                           page={page} nav={filteredNav} onOpenFiche={setFicheId} />
        )}
      >

      <ReportNavContext.Provider value={{ page, setPage, nav: filteredNav, goSettings }}>
      <main className="palestre-page-wrap">
      <ErrorBoundary key={`${page}:${planVersionId || 'current'}:${planningLevel(activeTenant)}`} onReset={() => setPage('home')}
                     context={toastContext.current}>
      {/* Client sans module vendu : le Cockpit est « toujours autorisé », il
          s'ouvrait donc quand même et allait chercher des données auxquelles le
          client n'a pas droit. On s'arrête avant. */}
      {page === 'welcome' && <WelcomePage visibleModules={visibleModules} setPage={setPage}
                                          onGoCockpitTab={handleGoCockpitTab}
                                          activeTenant={activeTenant} year={activeYear} />}
      {page === 'welcome-news' && <WelcomeNewsPage setPage={setPage}
                                                   onOpenPanel={() => setKnowledgeOpen(true)} />}
      {page === 'welcome-fiches' && <WelcomeFichesPage setPage={setPage} onOpenFiche={setFicheId} />}
      {page === 'welcome-guides' && <WelcomeGuidesPage visibleModules={visibleModules} setPage={setPage}
                                                       onOpenPanel={() => setKnowledgeOpen(true)} />}
      {page === 'home' && visibleModules.length === 0 && (
        <NoModulesPage tenantName={activeTenant?.name} canManage={isPlatformAdmin}
                       onGoSettings={() => setPage('admin-hub')} />
      )}
      {/* JOURNAL ET DÉCISIONS ont leur page (2026-10-01) : des notes, pas une
          vue d'analyse ; le Cockpit garde ses trois vues de données. */}
      {page === 'home' && visibleModules.length > 0 && JOURNAL_TABS.has(cockpitTab) && (
        <JournalPage activeTenant={activeTenant} view={cockpitTab} author={authUser?.name || ''}
                     canWrite={authScopes?.governance === 'rw'}
                     setPage={setPage} onGoCockpitTab={handleGoCockpitTab} />
      )}
      {page === 'home' && visibleModules.length > 0 && !JOURNAL_TABS.has(cockpitTab) && <CfoHub scopes={authScopes}                activeTenant={activeTenant} year={activeYear} onTenantPatched={handleTenantPatched} setPage={setPage} addToast={addToast} activeTab={cockpitTab} onTabChange={setCockpitTab} />}
      {/* Administration : PAGE PLEINE, plus une fenêtre (2026-08-26). Ses écrans
          (tables de clients, d'utilisateurs, de rôles) débordaient de la fenêtre
          modale. Ses SECTIONS vivent dans le panneau du menu, à la place des
          sections du module courant et alignées en bas, en vis-à-vis du bouton
          Administration qui ferme le rail (cf. Sidebar : `activeModule()`).
          Paramètres, lui, reste en fenêtre : ses cards sont étroites. */}
      {page === 'admin-hub'        && isPlatformAdmin && (
        <AdminHubPage addToast={addToast} onTenantsChanged={refreshTenantsList} activeTenant={activeTenant}
                      onTenantPatched={handleTenantPatched} onRenameTenant={handleRenameTenant}
                      onDeleteTenant={handleDeleteTenant} activeSection={adminTab} onSwitchTenant={handleTenantChange} />
      )}
      {page === 'pl-mapping'       && <MappingPage          activeTenant={activeTenant} addToast={addToast} />}
      {page === 'pl-adjustments'  && <AdjustmentsPage      activeTenant={activeTenant} addToast={addToast} year={activeYear} />}
      {page === 'bs-mapping'      && <BsMappingPage         activeTenant={activeTenant} addToast={addToast} />}
      {/* LES ÉTATS EN ACCÈS DIRECT (2026-09-17) : une section = un état, servi
          par le même composant (drapeau `only`, comme HrPlanningHub). Le
          contexte de lecture est ainsi partagé au niveau du module : la vue,
          l'arrêté et l'unité survivent au passage d'un état à l'autre. */}
      {FIN_STATE_PAGES[page] && <FinancialsReportsHub only={FIN_STATE_PAGES[page]} title={t(`nav:items.${page}`)} setPage={setPage}
                                                      activeTenant={activeTenant} addToast={addToast} year={activeYear} onYearChange={setActiveYear} />}
      {/* Le hub à six onglets reste ROUTÉ HORS MENU, le temps de la transition :
          plus rien n'y renvoie depuis que le Cockpit vise les sections. */}
      {page === 'financials-reports-hub' && <FinancialsReportsHub activeTenant={activeTenant} addToast={addToast} year={activeYear} onYearChange={setActiveYear} />}
      {page === 'financials-bfre' && <BfreCommandPage activeTenant={activeTenant} addToast={addToast} year={activeYear} />}
      {page === 'custom-reports'   && <CustomReportsPage     activeTenant={activeTenant} addToast={addToast} year={activeYear} onYearChange={setActiveYear} />}
      {/* Les trois sections du prévisionnel montent le MÊME hub : le sujet
          vient de la clé de page (2026-09-19). `forecast-hub` reste routé, le
          temps que les sessions ouvertes sur l'ancienne clé se referment. */}
      {['forecast-hub', 'forecast-wc', 'forecast-out', 'forecast-synth'].includes(page)
        && <PrevisionnelHub activeTenant={activeTenant} addToast={addToast} year={activeYear}
                            onYearChange={setActiveYear} scopes={authScopes} setPage={setPage} onGoSettings={goSettings}
                            subject={page === 'forecast-out' ? 'out' : page === 'forecast-synth' ? 'synth' : 'wc'} />}
      {page === 'fc-bs-hub' && <BalanceSheetCommandPage activeTenant={activeTenant} addToast={addToast} year={activeYear} onYearChange={setActiveYear} />}
      {page === 'fc-cf-hub' && <CashFlowCommandPage activeTenant={activeTenant} addToast={addToast} year={activeYear} onYearChange={setActiveYear} />}
      {isForecastStep(page) && <ForecastStepPage step={page} activeTenant={activeTenant} addToast={addToast} year={activeYear} onYearChange={setActiveYear} />}
      {page === 'financials-data-hub' && <FinancialsDataHub activeTenant={activeTenant} addToast={addToast} year={activeYear} onYearChange={setActiveYear} />}
      {page === 'financials-capex-plan' && <CapexPlanPage activeTenant={activeTenant} addToast={addToast} year={activeYear} onYearChange={setActiveYear}
                                                          withSummary={bandForPage(filteredNav, page)?.id === 'financials'} />}
      {page === 'fin-budget'       && <BudgetChargesPage activeTenant={activeTenant} addToast={addToast} year={activeYear} canWrite={authScopes?.financials === 'rw' || authScopes?.sales === 'rw'} />}
      {page === 'special-ops'      && <SpecialOperationsPage activeTenant={activeTenant} year={activeYear} canWrite={authScopes?.financials === 'rw'} />}
      {page === 'fin-payroll'      && <PayrollPage activeTenant={activeTenant} addToast={addToast} year={activeYear} />}
      {page === 'financials-assets' && <CapexPlanPage only="existing" activeTenant={activeTenant} addToast={addToast} year={activeYear} onYearChange={setActiveYear} />}
      {page === 'business-plan' && <BusinessPlanPage />}
      {page === 'fin-wc' && <WorkingCapitalPage activeTenant={activeTenant} year={activeYear} />}
      {page === 'financials-financing' && <FinancingSection activeTenant={activeTenant} addToast={addToast} year={activeYear} onYearChange={setActiveYear}
                                                            canWrite={authScopes?.financials === 'rw'} />}
      {page === 'tresorerie-hub' && <TresorerieHub activeTenant={activeTenant} addToast={addToast} year={activeYear} onYearChange={setActiveYear} />}
      {page === 'financials-input-hub' && <FinancialsInputHub activeTenant={activeTenant} setPage={setPage} addToast={addToast} year={activeYear} onYearChange={setActiveYear} />}
      {page === 'financials-mapping-hub' && <FinancialsMappingHub activeTenant={activeTenant} addToast={addToast} />}
      {page === 'financials-settings-hub' && <FinancialsSettingsHub activeTenant={activeTenant} onTenantPatched={handleTenantPatched} addToast={addToast} year={activeYear} />}
      {page === 'analytics-hub' && <AnalyticsHubPage activeTenant={activeTenant} addToast={addToast} year={activeYear} onYearChange={setActiveYear} />}
      {page === 'tdabc-total-cost-per-pool' && <CostAssignmentPage activeTenant={activeTenant} addToast={addToast} year={activeYear} onYearChange={setActiveYear} pageView="total" />}
      {page === 'cost-rates-hub'     && <RatesHub                     activeTenant={activeTenant} addToast={addToast} year={activeYear} onYearChange={setActiveYear} setPage={setPage} initialTab={navTab} />}
      {page === 'tdabc-rentabilite-client' && <RentabiliteClientPage  activeTenant={activeTenant} addToast={addToast} year={activeYear} onYearChange={setActiveYear} />}
      {page === 'chr-pcr'            && <CoutHeureTFHub               activeTenant={activeTenant} addToast={addToast} year={activeYear} onYearChange={setActiveYear} />}
      {page === 'treasury'         && <TreasuryPage          activeTenant={activeTenant} addToast={addToast} activeTab={cashTab} onTabChange={setCashTab} />}
      {page === 'sales-forecast'   && <SalesForecastPage     activeTenant={activeTenant} addToast={addToast} year={activeYear} onYearChange={setActiveYear} />}
      {page === 'sales-atterrissage' && (
        <AtterrissagePlanPage
          activeTenant={activeTenant}
          addToast={addToast}
          year={activeYear}
          canRead={authScopes.sales === 'r' || authScopes.sales === 'rw'}
          canWrite={authScopes.sales === 'rw'}
        />
      )}
      {page === 'sales-revenue' && (
        <AtterrissagePlanPage
          variant="revenue"
          activeTenant={activeTenant}
          addToast={addToast}
          year={activeYear}
          canRead={authScopes.sales === 'r' || authScopes.sales === 'rw'}
          canWrite={authScopes.sales === 'rw'}
          canWriteCosts={authScopes.financials === 'rw'}
        />
      )}
      {page === 'sales-offers-list' && (
        <OfferPlanPage activeTenant={activeTenant} year={activeYear} addToast={addToast}
                       canWrite={authScopes.sales === 'rw'} section="offres" />
      )}
      {page === 'sales-trajectory' && (
        <OfferPlanPage activeTenant={activeTenant} year={activeYear} addToast={addToast}
                       canWrite={authScopes.sales === 'rw'} />
      )}
      {page === 'sales-monthly' && (
        <OfferMonthPage activeTenant={activeTenant} year={activeYear} addToast={addToast}
                        canWrite={authScopes.sales === 'rw'}  onGoRealise={() => setPage('sales-billing')} />
      )}
      {page === 'sales-backlog' && (
        <PrevisionsPage activeTenant={activeTenant} year={activeYear} addToast={addToast}
                        canWrite={authScopes.sales === 'rw'} onGoRealise={() => setPage('sales-billing')} />
      )}
      {/* Hors menu depuis le 2026-10-09 : ouvre Prévisions des ventes sur la vue Portefeuille. */}
      {page === 'sales-portfolio' && (
        <OfferMonthPage activeTenant={activeTenant} year={activeYear} addToast={addToast}
                        canWrite={authScopes.sales === 'rw'} section="portefeuille" initialView="bk" />
      )}
      {page === 'sales-plan' && (
        <SalesPlanPage activeTenant={activeTenant} year={activeYear} addToast={addToast}
                      canWrite={authScopes.sales === 'rw'} />
      )}
      {page === 'direct-costs' && (
        <DirectCostsPage
          activeTenant={activeTenant}
          activeYear={activeYear}
          canWrite={authScopes.financials === 'rw'}
        />
      )}
      {/* « Stock & achats produits » : page RETIRÉE du routage le 2026-08-16 —
          elle écrivait la cible de coût des ventes et le plan de stock, deux
          pilotes concurrents des règles de ligne. Le composant reste dans le
          dépôt tant que la planification du stock n'est pas repensée. */}
      {page === 'sales-service-plan' && (
        <ServicePlanPage
          activeTenant={activeTenant}
          addToast={addToast}
          canRead={authScopes.sales === 'r' || authScopes.sales === 'rw'}
          canWrite={authScopes.sales === 'rw'}
        />
      )}
      {page === 'sales-source-maps' && <SourceMapPage activeTenant={activeTenant} addToast={addToast} canWrite={authScopes.sales === 'rw'} />}
      {page === 'sales-billing'    && <SalesBillingAnalysisPage activeTenant={activeTenant} addToast={addToast} year={activeYear} onYearChange={setActiveYear} />}
      {page === 'wip-home'         && <WipHomePage           setPage={setPage} />}
      {page === 'hr-time-pilot'    && <TimePilotHub         activeTenant={activeTenant} addToast={addToast} year={activeYear} />}
      {page === 'audit-log'        && isPlatformAdmin && <AuditLogPage addToast={addToast} />}
      {page === 'wip-news'         && isPlatformAdmin && <NewsAdminPage addToast={addToast} />}
      {/* PARAMÈTRES EN PAGE (2026-09-18) : ses sections vivent dans le panneau
          du menu, comme celles d'un module, et le contenu prend la zone de
          travail. La fenêtre qui l'a porté jusqu'ici RECOPIAIT le menu et la
          zone de travail par-dessus l'écran, sous un voile flouté que Safari
          recompose en continu. On ne quitte pas pour autant l'écran d'où l'on
          vient : `hubReturn` le retient, et « Retour » y ramène. */}
      {page === 'settings-hub'     && (
        <SettingsHubPage      setPage={setPage} year={activeYear} activeTenant={activeTenant} onTenantPatched={handleTenantPatched} onRenameTenant={handleRenameTenant} onDeleteTenant={handleDeleteTenant} addToast={addToast} activeSection={settingsTab} authUser={authUser} roles={activeRoleSlugs} scopes={authScopes} visibleModules={visibleModules}
                            onBack={() => setPage(hubReturn.current || 'home')}
                            backLabel={pageLabel(filteredNav, hubReturn.current, t)} />
      )}

      {/* ── Coming-soon placeholders ─────────────────────────────────────── */}
      {/* 'cockpit' is now the home cockpit — redirected to 'home' via useEffect above. */}

      {/* ── HR (read-only during transition) ─────────────────────────────── */}
      {page === 'hr-cost-simulation' && <HrCostSimulationPage activeTenant={activeTenant} setPage={setPage} year={activeYear} onYearChange={setActiveYear} />}
      {page === 'hr-evolution' && <HrEvolutionPage activeTenant={activeTenant} setPage={setPage} year={activeYear} onYearChange={setActiveYear} />}
      {page === 'hr-year-end-simulation' && <HrYearEndSimulationPage activeTenant={activeTenant} setPage={setPage} year={activeYear} onYearChange={setActiveYear} />}
      {page === 'hr-planning' && <HrPlanningHub only={FORECAST_TABS} title={t('nav:items.hr-planning')} activeTenant={activeTenant} setPage={setPage} addToast={addToast} year={activeYear} onYearChange={setActiveYear} onShowPlanningPrint={setPrintingPlanning} />}
      {page === 'hr-trajectory' && <HrPlanningHub only={TRAJECTORY_TABS} title={t('nav:items.hr-trajectory')} activeTenant={activeTenant} setPage={setPage} addToast={addToast} year={activeYear} onYearChange={setActiveYear} onShowPlanningPrint={setPrintingPlanning} />}
      {/* 'hr-people-hub' (ex-section Contrats, dissoute le 2026-08-21) reste
          accepté : un onglet de navigateur resté ouvert sur elle atterrit sur
          la section qui a repris ses onglets. */}
      {page === 'hr-expenses' && <PersonnelHub only={EXPENSE_TABS} title={t('nav:items.hr-expenses')}
                                               activeTenant={activeTenant} setPage={setPage} addToast={addToast}
                                               year={activeYear} onYearChange={setActiveYear}
                                               canWrite={authScopes.people === 'rw'} />}
      {(page === 'hr-personnel' || page === 'hr-people-hub') && <PersonnelHub only={SALARY_TABS} activeTenant={activeTenant} setPage={setPage} addToast={addToast} year={activeYear} onYearChange={setActiveYear} onShowPeoplePrint={setPrintingPeople}
                                               canWrite={authScopes.people === 'rw'} intent={peopleIntent} onIntentConsumed={() => setPeopleIntent(null)}
                                               onTenantUpdated={(t) => setTenants(prev => prev.map(x => x.id === t.id ? t : x))} />}
      {/* Réorganisation People 2026-08-21 : la Synthèse devient l'accueil du
          module, et les charges sociales une section à part entière. */}
      {page === 'hr-payroll-detail' && <PayrollDetailHub activeTenant={activeTenant} addToast={addToast} year={activeYear} onYearChange={setActiveYear} />}
      {page === 'hr-home' && <PeopleHomeHub activeTenant={activeTenant} setPage={setPage} addToast={addToast} userName={authUser?.name || null}
                                           year={activeYear} onYearChange={setActiveYear} onGoPeople={goPeople} onGoHeadcount={goHeadcount} />}
      {page === 'hr-headcount' && <HeadcountHub activeTenant={activeTenant} setPage={setPage} addToast={addToast} year={activeYear} onYearChange={setActiveYear} onGoPeople={goPeople}
                                               intent={headcountIntent} onIntentConsumed={() => setHeadcountIntent(null)} />}
      {page === 'hr-synthese' && <PeopleSynthesisPage activeTenant={activeTenant} setPage={setPage} year={activeYear} onYearChange={setActiveYear} addToast={addToast} onGoPeople={goPeople} />}
      {page === 'hr-story' && <PeopleStoryPage activeTenant={activeTenant} setPage={setPage} year={activeYear} onYearChange={setActiveYear}
                                               addToast={addToast} onGoPeople={goPeople} />}
      {/* LES DEUX ONGLETS RESTENT ICI (2026-09-27) : les taux de charges
          sociales se lisent aussi dans les Paramètres du client, parce que
          Finance et le plan de liquidité les consomment. Même vue, deux
          adresses — c'est la MÊME table, jamais une copie. */}
      {/* SECTION SANS ONGLET (2026-09-27) : une ligne d'onglets à un seul
          onglet est un choix qui n'en est pas un — le hub la masque. */}
      {page === 'hr-lpp' && <HrSocialHub activeTenant={activeTenant} setPage={setPage} addToast={addToast} year={activeYear} onYearChange={setActiveYear}
                                        only={['lpp']}
                                        onTenantUpdated={(t) => setTenants(prev => prev.map(x => x.id === t.id ? t : x))} />}
      {page === 'hr-social-hub' && <HrSocialHub activeTenant={activeTenant} setPage={setPage} addToast={addToast} year={activeYear} onYearChange={setActiveYear}
                                               only={['synthese', 'base', 'supp', 'phasing']}
                                               onTenantUpdated={(t) => setTenants(prev => prev.map(x => x.id === t.id ? t : x))} />}
      {/* Un seul hub, deux sections (2026-08-27) : le calendrier reste dans
          People, le temps standard est passé en tête de Rentabilité. */}
      {page === 'hr-time-hub' && <TimeHub only="calendar" title={t('nav:items.hr-time-hub')}
                                               activeTenant={activeTenant} addToast={addToast} year={activeYear} onYearChange={setActiveYear}
                                               onTenantUpdated={(t) => setTenants(prev => prev.map(x => x.id === t.id ? t : x))} />}
      {page === 'indirect-items' && <IndirectCostsPage activeTenant={activeTenant} addToast={addToast} year={activeYear} />}
      {page === 'cost-pools' && <RatesHub only="settings" title={t('nav:items.cost-pools')}
                                               activeTenant={activeTenant} addToast={addToast} year={activeYear}
                                               onYearChange={setActiveYear} setPage={setPage} />}
      {page === 'rc-services' && <RentabiliteClientPage only="settings" title={t('nav:items.rc-services')}
                                               activeTenant={activeTenant} addToast={addToast} year={activeYear} />}
      {page === 'rc-allocation' && <RentabiliteClientPage only="saisie" title={t('nav:items.rc-allocation')}
                                               activeTenant={activeTenant} addToast={addToast} year={activeYear} />}
      {page === 'time-standard' && <TimeHub only={TIME_ENTRY_TABS} title={t('nav:items.time-standard')}
                                               activeTenant={activeTenant} addToast={addToast} year={activeYear} onYearChange={setActiveYear} />}
      {page === 'hr-lcr-hub' && <LabourRateHub activeTenant={activeTenant} addToast={addToast} year={activeYear} onYearChange={setActiveYear} setPage={setPage} />}
      </ErrorBoundary>
      </main>
      </ReportNavContext.Provider>
      {/* Assistant : dans la PAGE, en bas à gauche, contre la navigation
          (2026-08-18) — posé dans .app-main, il suit le décalage quand la
          barre se déplie, sans calcul de largeur. */}
      <AssistantPanel tenant={activeTenant} year={activeYear} />
      </AppShell>
      </div>

      {toast && (
        <Toast key={toast.id} toast={toast} onDone={() => setToast(null)} />
      )}
      <FicheWindow ficheId={ficheId} onClose={() => setFicheId(null)} setPage={setPage}
                   activeTenant={activeTenant} year={activeYear} />
      <GuideOverlay setPage={setPage} onOpenFiche={setFicheId} nav={filteredNav} controls={!knowledgeOpen} />
      {printingTdabc && (
        <TdabcPrintDocument
          activeTenant={activeTenant}
          onClose={() => setPrintingTdabc(false)}
        />
      )}
      {printingPeople && (
        <PeoplePrintDocument
          activeTenant={activeTenant}
          onClose={() => setPrintingPeople(false)}
        />
      )}
      {printingPlanning && (
        <PlanningPrintDocument
          activeTenant={activeTenant}
          year={activeYear}
          onClose={() => setPrintingPlanning(false)}
        />
      )}
      {costingExport && (
        <CostingPrintDocument
          activeTenant={activeTenant}
          year={activeYear}
          mode={costingExport}
          onClose={() => setCostingExport(null)}
        />
      )}
      {printReports && printReports.reports.length > 0 && (
        <PrintReportDocument
          activeTenant={activeTenant}
          year={activeYear}
          reports={printReports.reports}
          mode={printReports.mode}
          onClose={() => setPrintReports(null)}
        />
      )}
    </ReportingContext.Provider>
  );
}
